import React from 'react';
import { api, ApiError } from '../api';
import { Field, Pill } from '../ui';
import { navigate, type Me } from '../App';

/**
 * The student setup page. A student lands here from their invitation link,
 * signs in to their own school account, and confirms their preferences.
 * A parent never enters a school password on the student's behalf.
 */
export function StudentSetupView({ me, onAuth }: { me: Me; onAuth: () => void }) {
  const params = new URLSearchParams(window.location.search);
  const token = params.get('token');
  const canvasResult = params.get('canvas');
  const canvasDetail = params.get('detail');
  const sportsOnly = window.location.hash === '#sports';

  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [redeemed, setRedeemed] = React.useState(false);
  const signedInAsOtherRole = me.authenticated && me.principal?.role !== 'student';

  React.useEffect(() => {
    if (!token || me.authenticated) return;
    setBusy(true);
    api('POST', '/api/auth/student/redeem', { token })
      .then(() => { setRedeemed(true); onAuth(); })
      .catch((e) => setError(e.message))
      .finally(() => setBusy(false));
  }, [token, me.authenticated, onAuth]);

  const studentId = me.principal?.studentId;
  const switchToStudentSetupInThisBrowser = async () => {
    if (!token) return;
    setBusy(true);
    setError(null);
    try {
      await api('POST', '/api/auth/logout');
      await api('POST', '/api/auth/student/redeem', { token });
      setRedeemed(true);
      onAuth();
    } catch (e: any) {
      setError(e.message ?? String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="card" style={{ maxWidth: 560, margin: '30px auto' }}>
      <h1 style={{ fontSize: 20, marginTop: 0 }}>
        {sportsOnly ? 'Set up your sports team' : 'Set up your Student Assistant'}
      </h1>

      {canvasResult === 'connected' && (
        <p className="banner ok">Canvas is connected. Your assignments will appear shortly.</p>
      )}
      {canvasResult === 'error' && (
        <p className="banner bad">
          Canvas did not finish connecting{canvasDetail ? `: ${canvasDetail}` : '.'} You can try again below.
        </p>
      )}

      {busy && <p role="status">Checking your link…</p>}
      {error && <p className="banner bad" role="alert">{error}</p>}

      {signedInAsOtherRole && token && (
        <div className="banner">
          This link is for the student account, but this browser is currently signed in as {me.principal?.role}.
          <div className="row" style={{ marginTop: 8 }}>
            <button className="primary" disabled={busy} onClick={switchToStudentSetupInThisBrowser}>
              Switch this browser to student setup
            </button>
            <button disabled={busy} onClick={() => navigate('/')}>Back to Family</button>
          </div>
          <p className="field-help" style={{ marginTop: 8 }}>
            You can also open this link in a private window on the student&apos;s device.
          </p>
        </div>
      )}

      {!me.authenticated && !busy && !redeemed && (
        <>
          <p>
            This page needs the setup link your parent shared with you. Open that link again, or ask
            them to create a new one.
          </p>
        </>
      )}

      {me.authenticated && studentId && (
        <>
          {!sportsOnly && (
            <p>
              You are signed in. Once your parent activates the Student Assistant, it can text you an after-school
              summary and an evening packing reminder. You can reply <strong>DONE</strong>, <strong>SNOOZE</strong>, <strong>HELP</strong>,
              or ask <em>“what should I pack?”</em> at any time. Reply <strong>STOP</strong> to turn off
              messages immediately.
            </p>
          )}

          {!sportsOnly && <CanvasStep studentId={studentId} />}

          {!sportsOnly && (
            <>
              <h2 style={{ fontSize: 16 }}>What happens next</h2>
              <ul>
                <li>Your parent verifies the phone number that receives reminders.</li>
                <li>You add your activities and packing lists.</li>
                <li>You see a preview of every reminder before the Student Assistant is activated.</li>
              </ul>
            </>
          )}

          <SportsStep studentId={studentId} />

          <button className="primary" onClick={() => navigate('/')}>Go to my Student Assistant</button>
        </>
      )}
    </main>
  );
}

function CanvasStep({ studentId }: { studentId: string }) {
  const [state, setState] = React.useState<any>(null);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    api('GET', '/api/school/readiness').then(setState).catch((e) => setError(e.message));
  }, []);

  if (error) return <p className="banner bad">{error}</p>;
  if (!state) return <p className="muted">Checking school connections…</p>;

  return (
    <div style={{ margin: '14px 0' }}>
      <h2 style={{ fontSize: 16 }}>
        Connect Canvas <Pill tone={state.canvas.configured ? 'ok' : 'warn'}>
          {state.canvas.configured ? 'ready' : 'not available yet'}
        </Pill>
      </h2>
      <p className="muted">{state.canvas.detail}</p>
      {state.canvas.configured ? (
        <button
          className="primary"
          onClick={async () => {
            try {
              const res = await api('GET', `/api/students/${studentId}/school/canvas/authorize-url`);
              window.location.href = res.url;
            } catch (e: any) { setError(e.message); }
          }}
        >
          Sign in to Canvas (read only)
        </button>
      ) : (
        <p>
          You can add tasks, activities, and packing lists while setting things up. The assistant will stay inactive
          until Canvas is connected and its first successful assignment sync is complete.
        </p>
      )}
      <details>
        <summary className="muted">What will be read</summary>
        <ul>
          <li>Your courses, assignment titles, due dates and submission status.</li>
          <li>Nothing is submitted or changed on your behalf, ever.</li>
          <li>No grades of other students and no teacher messages.</li>
        </ul>
      </details>
    </div>
  );
}

/**
 * Self-service sports schedule setup, reached via the link the assistant
 * texts when it has a sports question but no team on file. Deliberately a
 * simple setup form; the server performs the ArbiterLive lookup from the
 * saved school, sport, level, and team name before falling back to details.
 */
function SportsStep({ studentId }: { studentId: string }) {
  const [teams, setTeams] = React.useState<any[]>([]);
  const [sport, setSport] = React.useState('');
  const [level, setLevel] = React.useState('');
  const [teamName, setTeamName] = React.useState('');
  const [opponent, setOpponent] = React.useState('');
  const [startsAt, setStartsAt] = React.useState('');
  const [location, setLocation] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [linkUnavailable, setLinkUnavailable] = React.useState(false);
  const [saved, setSaved] = React.useState<string | null>(null);

  const loadTeams = React.useCallback(async () => {
    const data = await api<any>('GET', `/api/students/${studentId}/sports`);
    setTeams(data.teams ?? []);
    setLinkUnavailable(false);
  }, [studentId]);

  React.useEffect(() => {
    loadTeams().catch((e: any) => {
      if (e instanceof ApiError && e.status === 404) {
        setLinkUnavailable(true);
        setError(null);
        return;
      }
      setError(e.message ?? String(e));
    });
  }, [loadTeams]);

  const save = async () => {
    setError(null);
    if (!sport.trim() || !teamName.trim()) {
      setError('Sport and team name are required.');
      return;
    }
    setBusy(true);
    try {
      const { teamId } = await api('POST', `/api/students/${studentId}/sports/manual-team`, {
        sport: sport.trim(),
        level: level.trim() || undefined,
        teamName: teamName.trim(),
      });
      if (startsAt.trim()) {
        await api('POST', `/api/students/${studentId}/sports/teams/${teamId}/next-game`, {
          opponent: opponent.trim() || undefined,
          startsAt: new Date(startsAt).toISOString(),
          location: location.trim() || undefined,
        });
      }
      await loadTeams();
      setSaved(`${sport.trim()} · ${level.trim() || 'team'} · ${teamName.trim()} saved.`);
      setSport('');
      setLevel('');
      setTeamName('');
      setOpponent('');
      setStartsAt('');
      setLocation('');
    } catch (e: any) {
      setError(e.message ?? String(e));
    } finally {
      setBusy(false);
    }
  };

  const removeTeam = async (team: any) => {
    setBusy(true);
    setError(null);
    try {
      await api('DELETE', `/api/students/${studentId}/sports/teams/${team.id}`);
      await loadTeams();
      setSaved(`${team.sport} · ${team.team_name} removed.`);
    } catch (e: any) {
      if (e instanceof ApiError && e.status === 404) {
        setLinkUnavailable(true);
        setError(null);
      } else {
        setError(e.message ?? String(e));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ margin: '14px 0' }}>
      <h2 style={{ fontSize: 16 }}>Sports schedule</h2>
      <p className="muted">
        Add your sport and team so the Student Assistant can answer "when is my next game" and
        packing questions by text. This takes about a minute.
      </p>

      {linkUnavailable ? (
        <div className="banner bad" role="alert">
          This sports setup link is no longer valid. Ask the parent to send a new setup link, then reopen it.
        </div>
      ) : (
        <>
      {saved && <p className="banner ok">{saved} You can add another team below.</p>}
      {error && <p className="banner bad" role="alert">{error}</p>}
      {teams.length > 0 && (
        <>
          <h3 style={{ fontSize: 14 }}>Your teams</h3>
          <ul className="plain">
            {teams.map((team) => (
              <li key={team.id} className="spread">
                <strong>{[team.sport, team.team_level, team.team_name].filter(Boolean).join(' · ')}</strong>
                <button disabled={busy} onClick={() => removeTeam(team)}>Remove</button>
              </li>
            ))}
          </ul>
          <h3 style={{ fontSize: 14 }}>Add another sport or team</h3>
        </>
      )}
      <label className="field">
        <span>Sport</span>
        <select value={sport} onChange={(e) => setSport(e.target.value)}>
          <option value="">Select a sport</option>
          <option value="Baseball">Baseball</option>
          <option value="Basketball">Basketball</option>
          <option value="Cross Country">Cross Country</option>
          <option value="Football">Football</option>
          <option value="Golf">Golf</option>
          <option value="Lacrosse">Lacrosse</option>
          <option value="Soccer">Soccer</option>
          <option value="Softball">Softball</option>
          <option value="Swimming">Swimming</option>
          <option value="Tennis">Tennis</option>
          <option value="Track">Track</option>
          <option value="Volleyball">Volleyball</option>
          <option value="Wrestling">Wrestling</option>
          <option value="Other">Other</option>
        </select>
      </label>
      <Field label="Team level (optional)" value={level} onChange={setLevel} placeholder="JV, Varsity, Middle School" />
      <Field label="Team name" value={teamName} onChange={setTeamName} placeholder="Royal" />
      <p className="muted">We will look up the team schedule automatically. You do not need to provide a schedule link.</p>
      <h3 style={{ fontSize: 14, marginBottom: 4 }}>Next game (optional, add or update anytime)</h3>
      <Field label="Opponent" value={opponent} onChange={setOpponent} placeholder="Lakeside" />
      <label className="field">
        <span>Date and time</span>
        <input type="datetime-local" value={startsAt} onChange={(e) => setStartsAt(e.target.value)} />
      </label>
      <Field label="Location" value={location} onChange={setLocation} placeholder="Home gym" />
      <button className="primary" disabled={busy} onClick={save} style={{ marginTop: 8 }}>
        {busy ? 'Saving…' : teams.length ? 'Add team' : 'Save sports info'}
      </button>
        </>
      )}
    </div>
  );
}


export function StudentPrefs({ onSave }: { onSave: (v: string) => void }) {
  const [v, setV] = React.useState('');
  return <Field label="Preference" value={v} onChange={(x) => { setV(x); onSave(x); }} />;
}
